import { BCMSCurrency, Branding, DisabledPrompt, Link, useModel } from "@bcm-dev/bcms-vite";
import Util from "classes/Util";
import { TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableView, WidgetContainer, WidgetView } from "components/shared";
import { Account, CheckingAccount, CODAccount, LoanAccount, LOCAccount, SavingsAccount } from "model";
import React, { useEffect, useState } from "react";
import AccountSummaryBrandableLinksWidget from "../AccountSummaryBrandableLinksWidget";
import AccountSummaryAccountCustomLinksWithTableRowWidget from "../AccountSummarySortableTableWithLinksWidget/components/AccountSummaryAccountCustomLinksWithTableRowWidget";
import "./styles.css";
import AccountSummaryTotalRow from "../AccountSummaryTotalRow";

/**
 * @function AccountSummarySortableTableWidget
 * @return {React.Component}
 * @exported true
 * @description This is the main widget of the AccountSummarySortableTableWidget
 */
export default function AccountSummarySortableTableWidget(props) {
    const { branding } = useModel();
    const [ isDesktop, setIsDesktop ] = useState(window.innerWidth >= 751);
    const isMobile = !isDesktop; 
    const { options } = branding;
    const hasSectionHeaders = options.section_headers_abi22 === "true";
    const hasDesktopAvailableBalanceOn = options.availbal_abi22 === "true";
    const hasDesktopCurrentBalanceOn = options.currentbal_abi22 === "true";
    const hasDesktopTotalAccountBalancesOn = options.accttotals_acctsum_abi22 === "true";
    let hasTotalAccountBalancesOn = false;
    let hasAvailableBalanceOn = false;
    let hasCurrentBalanceOn = false;
    const totalAccountBalancesPosition = options.account_totals_above_or_below_abi22 === "undefined" ? "Above" : options.account_totals_above_or_below_abi22;
    const showHeaderTotalAccountBalances = totalAccountBalancesPosition === "Above";
  
    // "isDesktop" is dynamically updated in the "useEffect" hook below.
    if(isDesktop) {
      hasTotalAccountBalancesOn = hasDesktopTotalAccountBalancesOn;
      hasAvailableBalanceOn = hasDesktopAvailableBalanceOn;
      hasCurrentBalanceOn = hasDesktopCurrentBalanceOn;
    } else if(isMobile) {
      // The mobile version of the total balance option would be read here.
      // Using "false" for now since a mobile option doesnt exist yet.
      hasTotalAccountBalancesOn = false;
      hasAvailableBalanceOn = true;
      hasCurrentBalanceOn = false;
    }

    // Create the Summary items for each type of account
    const hasLOCAccounts = options.loc_primary_abi22 === "true";
    const firstAccounts = options.loc_primary_abi22 === "true" ? LOCAccount.initAll() : CheckingAccount.initAll();
    const hasCustomLinks = props.showCustomLinks ?? false;
    const showCustomLinks = props.showCustomLinks ? { showCustomLinks: props.showCustomLinks } : {};

    const allAccounts = [...firstAccounts, ...(hasLOCAccounts ? [] : SavingsAccount.initAll()), ...CODAccount.initAll(), ...LoanAccount.initAll()];
    const firstAccts = firstAccounts.map(account => <AccountSummarySortableView key={ "account_" + account.id } account ={account} 
        accounts={ hasSectionHeaders ? firstAccounts : allAccounts } isDesktop={ isDesktop }
        hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } { ...showCustomLinks } />, []);
    
    let savingsAccts = [];

    if(!hasLOCAccounts) {
        savingsAccts = SavingsAccount.initAll().map(account => <AccountSummarySortableView key={ "account_" + account.id } account ={account} 
            accounts={ hasSectionHeaders ? SavingsAccount.initAll() : allAccounts } isDesktop={ isDesktop }
            hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } { ...showCustomLinks } />, []);
    }
    
    const codAccounts = CODAccount.initAll().map(account => <AccountSummarySortableView key={ "account_" + account.id }  account ={account} 
        accounts={ hasSectionHeaders ? CODAccount.initAll() : allAccounts } isDesktop={ isDesktop }
        hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } { ...showCustomLinks } />, []);
    
    const loanAccts = LoanAccount.initAll().map(account => <AccountSummarySortableView key={ "account_" + account.id }account ={account} 
        accounts={ hasSectionHeaders ? LoanAccount.initAll() : allAccounts } isDesktop={ isDesktop }
        hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } { ...showCustomLinks } />, []);

    const totalAccountBalances = (accountType, accounts, showAvailableBalance, showCurrentBalance, showAvailableCredit) => {
        const accumulator = { 
            "accountType": accountType,
            "availableBalance": (showAvailableBalance === false || showAvailableBalance === undefined) ? undefined : 0, 
            "availableCredit": (showAvailableCredit === false || showAvailableCredit === undefined) ? undefined : 0, 
            "currentBalance": (showCurrentBalance === false || showCurrentBalance === undefined) ? undefined : 0,
            "availableBalanceDataPoint": "",
            "availableCreditDataPoint": "",
            "currentBalanceDataPoint": ""
        };

        return accounts.reduce((acc, item) => {
            if(acc.availableBalance !== undefined) {
                if(Account.showBalanceTypeDependingOnViewType(item, "available", isDesktop ? "desktop" : "mobile")) {
                    acc.availableBalance += parseFloat(item.availableBalance ? item.availableBalance.amount : 0);
                }
            }
            if(acc.currentBalance !== undefined) {
                if(Account.showBalanceTypeDependingOnViewType(item, "current", isDesktop ? "desktop" : "mobile")) {
                    acc.currentBalance += parseFloat(item.currentBalance ? item.currentBalance.amount : 0);
                }
            }
            if(acc.availableCredit !== undefined) {
                acc.availableCredit += parseFloat(item.availableCredit ? item.availableCredit.amount : 0);
            }
            return acc;
        }, accumulator);
    };

    // If any account has a balance by "type" (availble/current, ect.) 
    // branded to be on, the total balance of that type is shown.
    const showTotalBalanceByType = (type, accounts) => {
        return accounts.filter(account => {
            return Account.showBalanceTypeDependingOnViewType(account, type, isDesktop ? "desktop" : "mobile");
        }).length > 0;
    };

    const totalCheckingAccountBalancesObj = totalAccountBalances("checking", firstAccounts, showTotalBalanceByType("available", firstAccounts), showTotalBalanceByType("current", firstAccounts));
    let totalSavingsAccountBalancesObj = {};

    if(!hasLOCAccounts) {
        totalSavingsAccountBalancesObj = totalAccountBalances("savings", SavingsAccount.initAll(), showTotalBalanceByType("available", SavingsAccount.initAll()), showTotalBalanceByType("current", SavingsAccount.initAll()));
    }
    
    const totalCODAccountBalancesObj = totalAccountBalances("cod", CODAccount.initAll(), showTotalBalanceByType("available", CODAccount.initAll()), showTotalBalanceByType("current", CODAccount.initAll()));
    //const totalLoanAccountBalancesObj = totalAccountBalances("loan", LoanAccount.initAll(), showTotalBalanceByType("available", LoanAccount.initAll()), showTotalBalanceByType("current", LoanAccount.initAll()), false);

    // START: Assets/Loans sections.
    const showAssetsAndLoansUI = String(props.showAssetsAndLoansUI ?? "false") === "true";
    let allAssetAccounts = [];
    let allLoanAccounts = [];

    if(hasLOCAccounts) {
        allLoanAccounts = [ ...LOCAccount.initAll(), ...LoanAccount.initAll() ]
    } else {
        allAssetAccounts = [ ...CheckingAccount.initAll(), ...SavingsAccount.initAll() ]
        allLoanAccounts = LoanAccount.initAll();
    }

    allAssetAccounts = allAssetAccounts.concat(CODAccount.initAll());
    const totalAssetAvailableBalancesObj = totalAccountBalances("all", allAssetAccounts , showTotalBalanceByType("available", allAssetAccounts), showTotalBalanceByType("current", allAssetAccounts));
    const totalLoanAccountBalancesObj = totalAccountBalances("loan", allLoanAccounts, showTotalBalanceByType("available", LoanAccount.initAll()), showTotalBalanceByType("current", LoanAccount.initAll()), false);

    const allAssetAccountViews = allAssetAccounts.map(account => <AccountSummarySortableView key={ "account_" + account.id } account ={account} 
    accounts={ hasSectionHeaders ? allAssetAccounts : allAccounts } isDesktop={ isDesktop }
    hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />, []);

    const allLoanAccountViews = allLoanAccounts.map(account => <AccountSummarySortableView key={ "account_" + account.id }account ={account} 
    accounts={ hasSectionHeaders ? allLoanAccounts : allAccounts } isDesktop={ isDesktop }
    hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />, []);
    // END: Assets/Loans sections.

    useEffect(() => {
        // Detect window resize to apply desktop only options and mobile only options.
        const onWindowResize = () => setIsDesktop(window.innerWidth >= 751);
        window.addEventListener("resize", onWindowResize);
        onWindowResize();
        return () => window.removeEventListener("resize", onWindowResize);
    });

    return (
        <WidgetContainer className={ `account-summary-sortable-table-widget ${ hasCustomLinks ? "custom-links-style" : "" } ` + props.className} >
            <WidgetView text="Account Summary">
            <AccountSummaryBrandableLinksWidget/>
                <AccountSummarySortableTable>

                    {/* START: Assets/Loans sections. */}
                    { showAssetsAndLoansUI && 
                    <>
                        { hasSectionHeaders && 
                        <>
                            <AccountSummarySortableHeader dataPoint="Assets_Label" text={ `Assets` }
                            hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn }
                            showTotalAccountBalances={ showHeaderTotalAccountBalances } 
                            totalAccountBalancesObj={ totalAssetAvailableBalancesObj } />

                            <AccountSummarySortableColumnHeader
                            accounts={ allAssetAccounts }
                            isDesktop={ isDesktop }
                            hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />
                            { allAssetAccountViews }
                            <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                            showTotalAccountBalances={ !showHeaderTotalAccountBalances }
                            totalColumns={ 4 }
                            totalAccountBalancesObj={ totalAssetAvailableBalancesObj } />
                            { !Util.inMabi && 
                            <Branding id="acctsumwidget_order_checks_abi22" value="true" default="false">
                                <>
                                <DisabledPrompt>
                                    <Link>
                                    <span data-point="Order_Checks" className="order-checks-link">Order Checks</span>
                                    </Link>
                                </DisabledPrompt>
                                </>
                            </Branding>
                            }

                            {/* Loans. */}
                            <AccountSummarySortableHeader dataPoint="Loans" text="Loans" 
                            hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                            showTotalAccountBalances={ showHeaderTotalAccountBalances } 
                            totalAccountBalancesObj={ totalLoanAccountBalancesObj } />
                            
                            <AccountSummarySortableColumnHeader
                            accounts={ allLoanAccounts }
                            isDesktop={ isDesktop }
                            hasAvailableBalanceOn={ false } hasCurrentBalanceOn={ true } />
                            { allLoanAccountViews }
                            <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                            showTotalAccountBalances={ !showHeaderTotalAccountBalances }
                            totalColumns={ 4 }
                            totalAccountBalancesObj={ totalLoanAccountBalancesObj } />
                        </>
                        }
                    </>
                    }
                    {/* END: Assets/Loans sections. */}

                    {/* START: Default UI. Branded assets/Loans sections are not shown. */}
                    { !showAssetsAndLoansUI && 
                    <>
                        { hasSectionHeaders && 
                        <>
                            <AccountSummarySortableHeader dataPoint="Checking_Accounts" text={ hasLOCAccounts ? `Line of Credit` : `Checking Accounts` }
                            hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                            showTotalAccountBalances={ showHeaderTotalAccountBalances } 
                            totalAccountBalancesObj={ totalCheckingAccountBalancesObj } />

                            <AccountSummarySortableColumnHeader
                            accounts={ firstAccounts }
                            isDesktop={ isDesktop }
                            hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />
                            {firstAccts}
                            <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                            showTotalAccountBalances={ !showHeaderTotalAccountBalances }
                            totalColumns={ 4 }
                            totalAccountBalancesObj={ totalCheckingAccountBalancesObj } />
                            { !Util.inMabi && 
                            <Branding id="acctsumwidget_order_checks_abi22" value="true" default="false">
                                <>
                                <DisabledPrompt>
                                    <Link>
                                    <span data-point="Order_Checks" className="order-checks-link">Order Checks</span>
                                    </Link>
                                </DisabledPrompt>
                                </>
                            </Branding>
                            }
                            
                            { !hasLOCAccounts &&
                            <>
                                <AccountSummarySortableHeader text="Saving Accounts" 
                                hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                                showTotalAccountBalances={ showHeaderTotalAccountBalances } 
                                totalAccountBalancesObj={ totalSavingsAccountBalancesObj } />

                                <AccountSummarySortableColumnHeader
                                accounts={ SavingsAccount.initAll() }
                                isDesktop={ isDesktop }
                                hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />
                                {savingsAccts}
                                <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                                showTotalAccountBalances={ !showHeaderTotalAccountBalances }
                                totalColumns={ 4 }
                                totalAccountBalancesObj={ totalSavingsAccountBalancesObj } />
                            </>
                            }

                            <AccountSummarySortableHeader text="Certificate of Deposit" 
                            hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                            showTotalAccountBalances={ showHeaderTotalAccountBalances } 
                            totalAccountBalancesObj={ totalCODAccountBalancesObj } />

                            <AccountSummarySortableColumnHeader
                            accounts={ CODAccount.initAll() }
                            isDesktop={ isDesktop }
                            hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />
                            {codAccounts}
                            <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                            showTotalAccountBalances={ !showHeaderTotalAccountBalances }
                            totalColumns={ 4 }
                            totalAccountBalancesObj={ totalCODAccountBalancesObj } />

                            {/* Only the total balance is brandable for loans. 
                            The current balance in particular is not brandable for loans yet. */}
                            <AccountSummarySortableHeader dataPoint="Loans" text="Loans" 
                            hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                            showTotalAccountBalances={ showHeaderTotalAccountBalances } 
                            totalAccountBalancesObj={ totalLoanAccountBalancesObj } />
                            
                            <AccountSummarySortableColumnHeader
                            accounts={ LoanAccount.initAll() }
                            isDesktop={ isDesktop }
                            hasAvailableBalanceOn={ false } hasCurrentBalanceOn={ true } />
                            {loanAccts}
                            <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                            showTotalAccountBalances={ !showHeaderTotalAccountBalances }
                            totalColumns={ 4 }
                            totalAccountBalancesObj={ totalLoanAccountBalancesObj } />
                        </>
                        }
                    </>
                    }
                    {/* END: Default UI. Branded assets/Loans sections are not shown. */}

                    { !hasSectionHeaders && 
                    <>
                        <AccountSummarySortableColumnHeader
                        accounts={ allAccounts }
                        isDesktop={ isDesktop }
                        hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />
                        {firstAccts}
                        <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                        showTotalAccountBalances={ !showHeaderTotalAccountBalances }
                        totalColumns={ 4 }
                        totalAccountBalancesObj={ totalCheckingAccountBalancesObj } />
                        { !Util.inMabi && 
                        <Branding id="acctsumwidget_order_checks_abi22" value="true" default="false">
                            <>
                            <DisabledPrompt>
                                <Link>
                                <span data-point="Order_Checks" className="order-checks-link">Order Checks</span>
                                </Link>
                            </DisabledPrompt>
                            </>
                        </Branding>
                        }
                        { !hasLOCAccounts && savingsAccts }
                        <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                        showTotalAccountBalances={ !showHeaderTotalAccountBalances }
                        totalColumns={ 4 }
                        totalAccountBalancesObj={ totalSavingsAccountBalancesObj } />
                        {codAccounts}
                        <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                        showTotalAccountBalances={ !showHeaderTotalAccountBalances }
                        totalColumns={ 4 }
                        totalAccountBalancesObj={ totalCODAccountBalancesObj } />
                        {loanAccts}
                        <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
                        showTotalAccountBalances={ !showHeaderTotalAccountBalances }
                        totalColumns={ 4 }
                        totalAccountBalancesObj={ totalLoanAccountBalancesObj } />
                    </>
                    }
                </AccountSummarySortableTable>
            </WidgetView>
        </WidgetContainer>
    )
}

/**
 * @function AccountSummarySortableHeader
 * @return {React.Component}
 * @exported true
 * @description This components is the table body for the AccountSummarySortableTableWidget.
 */
export function AccountSummarySortableTable(props) {
    return (
        <TableView className="account-summary-sortable-table">
            {props.children}
        </TableView>
    );
}

/**
 * @function AccountSummarySortableHeader
 * @return {React.Component}
 * @exported true
 * @description This components shows the account type header.
 */
export function AccountSummarySortableHeader(props) {
    const text = props.text ?? "";
    const dataPoint = props.dataPoint ? { "dataPoint" : props.dataPoint } : {};
    const hasTotalAccountBalancesOn = props.hasTotalAccountBalancesOn ?? false;
    const totalAccountBalancesObj = props.totalAccountBalancesObj ?? {};
    const showTotalAccountBalances = props.showTotalAccountBalances ?? true;

    // Returns a total balances string like "$26,125.00 | $26,125.00"
    // for the total available balance (left) and the total current balance (right).
    // Only loans have "availableCredit" but that balance not being used at the moment.
    const totalBalanceMessage = obj => {
        let message = "";

        if(obj.availableBalance !== undefined) {
        message += `${ BCMSCurrency.init(obj.availableBalance).format() } | `;
        }
        if(obj.availableCredit !== undefined) {
        message += `${ BCMSCurrency.init(obj.availableCredit).format() } | `;
        }
        if(obj.currentBalance !== undefined) {
        message += `${ BCMSCurrency.init(obj.currentBalance).format() } | `;
        }
        message = message.slice(0, -2);
        return message;
    }
    
    const totalAccountBalanceText = totalBalanceMessage(totalAccountBalancesObj);

    return (
        <TableColumnContainer className="account-summary-sortable-header">
            { (hasTotalAccountBalancesOn && showTotalAccountBalances) && 
                <>
                    <TableColumnView className="text-left" { ...dataPoint } text={text} colSpan="2" />
                    <TableColumnView className="text-right total-balances" text={ totalAccountBalanceText } colSpan="2" flexClassNames="" />
                </>
            }
            { !(hasTotalAccountBalancesOn && showTotalAccountBalances) && 
                <>
                    <TableColumnView className="text-left" { ...dataPoint } text={text} />
                    <TableColumnView />
                    <TableColumnView />
                    <TableColumnView />
                </>
            }
        </TableColumnContainer>
    );
}

/**
 * @function AccountSummarySortableColumnHeader
 * @return {React.Component}
 * @exported true
 * @description This components shows the table sortable column headers
 */
export function AccountSummarySortableColumnHeader(props) {
    const accounts = props.accounts ?? [];
    const isDesktop = props.isDesktop ?? true;

    // If any account has available balance branded to be on, the available balance column is shown.
    const showAvailableBalanceColumn = accounts.filter(account => {
        return Account.showBalanceTypeDependingOnViewType(account, "available", isDesktop ? "desktop" : "mobile");
    }).length > 0;

    // If any account has current balance branded to be on, the current balance column is shown.
    const showCurrentBalanceColumn = accounts.filter(account => {
        const res = Account.showBalanceTypeDependingOnViewType(account, "current", isDesktop ? "desktop" : "mobile");
        return res;
    }).length > 0;

    return (
        <TableColumnContainer className="account-summary-sortable-column-header">
            <TableColumnView className="text-left nickname" text="Account Nickname" icon="SortIcon" />
            { showAvailableBalanceColumn ? false : <TableColumnView text="" />}
            { showCurrentBalanceColumn ? false : <TableColumnView text="" />}
            <TableColumnView flexClassNames="flex-container flex-horizontal justify-right" className="account-number" text="Account Number" icon="SortIcon" />
            { showAvailableBalanceColumn ? <TableColumnView flexClassNames="flex-container flex-horizontal justify-right" className="available-balance" text="Available Balance" icon="SortIcon" /> : false}
            { showCurrentBalanceColumn ? <TableColumnView flexClassNames="flex-container flex-horizontal justify-right" className="current-balance" text="Current Balance" icon="SortIcon" /> : false}
        </TableColumnContainer>
    );
}

/**
 * @function AccountSummarySortableView
 * @param {STRING} nickname - Sets the account nickname
 * @param {STRING} accountNumber - Sets the four digit account number.
 * @param {STRING} availableBalance - Sets the available balance for the account.
 * @param {STRING} currentBalance - Sets the current balance for the account.
 * @return {React.Component}
 * @exported true
 * @description This component shows each account and account details.
 */
export function AccountSummarySortableView(props) {
    const account = props.account ?? {};
    const accountID = account.id ?? "";
    let defaultView =  window.innerWidth <= 750? "mobile_":"Desktop_";
    const accountNumber = account.number ?? "[Account Number]";
    const availableBalance = account.availableBalance ? account.availableBalance.format() : false;
    const currentBalance = account.currentBalance ? account.currentBalance.format() : false;
    const accountNicknameLink = <Link to={"/accountdetails/" + account.id} dataPoint={ `Account_Name_${defaultView+ accountID }` }>{account.nickname}</Link>;
    const accounts = props.accounts ?? [];
    const isDesktop = props.isDesktop ?? true;
    const currAccountHasAvailableBalanceOn = Account.showBalanceTypeDependingOnViewType(account, "available", isDesktop ? "desktop" : "mobile");
    const currAccountHasCurrentBalanceOn = Account.showBalanceTypeDependingOnViewType(account, "current", isDesktop ? "desktop" : "mobile");
    const showCustomLinks = props.showCustomLinks ?? false;
    const accountTypeMap = {
        checking: "Checking",
        savings: "Savings",
        loan: "Loan",
        cod: "COD",
        loc: "LOC"
    };
    const accountTypeCustomLinks = accountTypeMap[account.type];

    // If any account has available balance branded to be on, the available balance column is shown.
    const showAvailableBalanceColumn = accounts.filter(account => {
        return Account.showBalanceTypeDependingOnViewType(account, "available", isDesktop ? "desktop" : "mobile");
    }).length > 0;

    // If any account has current balance branded to be on, the current balance column is shown.
    const showCurrentBalanceColumn = accounts.filter(account => {
        const res = Account.showBalanceTypeDependingOnViewType(account, "current", isDesktop ? "desktop" : "mobile");
        return res;
    }).length > 0;

    let accountNumberDataPoint = `Account_Number_${defaultView+ accountID }`;
    let accountAvailableBalanceDataPoint = `Account_Balance_${ defaultView+accountID }`;
    let accountCurrentBalanceDataPoint = `Account_Current_Balance_${ defaultView+accountID }`;
  
    // If "Available Balance" is off, move the data point on that amount to the "Current Balance" amount.
    // This is because this particular data point is used in the following captures:
    // https://onlinebanktours.com/bcms/edit/pages/captures/edit.php?id=14801
    // https://onlinebanktours.com/bcms/edit/pages/captures/edit.php?id=20268
    if(!currAccountHasAvailableBalanceOn) {
      accountCurrentBalanceDataPoint = accountAvailableBalanceDataPoint;
    }

    return (
        <TableBody>
            <TableItemContainer className="account-summary-sortable-view clickable">
                <TableItemTextView className="text-left nickname" text={ accountNicknameLink } />
                { showAvailableBalanceColumn ? false : <TableItemTextView text="" />}
                { showCurrentBalanceColumn ? false : <TableItemTextView text="" />}
                <TableItemTextView className="text-right account-number" data-point={ accountNumberDataPoint } text={accountNumber} />
                { showAvailableBalanceColumn ? <TableItemTextView className="text-right available-balance" data-point={ accountAvailableBalanceDataPoint } text={ currAccountHasAvailableBalanceOn ? availableBalance : "" } /> : false}
                { showCurrentBalanceColumn ? <TableItemTextView className="text-right current-balance" data-point={ accountCurrentBalanceDataPoint } text={ currAccountHasCurrentBalanceOn ? currentBalance : "" } /> : false}
            </TableItemContainer>
            { showCustomLinks
            ? <AccountSummaryAccountCustomLinksWithTableRowWidget type={ accountTypeCustomLinks } />
            : undefined }
        </TableBody>
    );
}   

